import { get, postJsonAdmin } from './_core.js';

export interface UserListItem {
  id: string;
  email: string;
  role: string;
  createdAt: string;
  lastSessionAt: string | null;
  creditsBalanceNano: string;
  spend7dNano: string;
}

export interface UserListFilters {
  q?: string;
  role?: string;
  limit?: number;
  cursor?: string;
  /** Fase 39 — default false (oculta service accounts del simulator). */
  includeSynthetic?: boolean;
}

export interface UserListResponse {
  items: UserListItem[];
  nextCursor: string | undefined;
}

export async function fetchUsers(filters: UserListFilters = {}): Promise<UserListResponse> {
  const params = new URLSearchParams();
  if (filters.q) params.set('q', filters.q);
  if (filters.role) params.set('role', filters.role);
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.cursor) params.set('cursor', filters.cursor);
  if (filters.includeSynthetic) params.set('include_synthetic', 'true');
  const qs = params.toString();
  const res = await get<UserListItem[]>(`/admin/users${qs ? `?${qs}` : ''}`);
  return { items: res.data, nextCursor: res.meta?.nextCursor };
}

export interface UserDetail {
  id: string;
  email: string;
  role: string;
  name: string | null;
  locale: string;
  timezone: string;
  createdAt: string;
  billingMode: string;
  creditsBalanceNano: string;
  spend7dNano: string;
  byokKeys: Array<{
    providerId: string;
    label: string | null;
    createdAt: string;
    lastUsedAt: string | null;
    lastValidatedAt: string | null;
  }>;
}

export async function fetchUserDetail(id: string): Promise<UserDetail> {
  const res = await get<UserDetail>(`/admin/users/${encodeURIComponent(id)}`);
  return res.data;
}

export interface UserTransaction {
  id: string;
  kind: string;
  amountNano: string;
  balanceAfterNano: string;
  notes: string | null;
  callKind: string | null;
  createdAt: string;
}

export async function fetchUserTransactions(
  id: string,
  opts: { limit?: number; cursor?: string } = {},
): Promise<{ items: UserTransaction[]; nextCursor: string | undefined }> {
  const params = new URLSearchParams();
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.cursor) params.set('cursor', opts.cursor);
  const qs = params.toString();
  const res = await get<UserTransaction[]>(
    `/admin/users/${encodeURIComponent(id)}/transactions${qs ? `?${qs}` : ''}`,
  );
  return { items: res.data, nextCursor: res.meta?.nextCursor };
}

// ─── BO-5 A — agregaciones / time-series ─────────────────────────────────

export interface ActivitySparkBucket {
  day: string;
  sessionCount: number;
  costNano: string;
}

export async function fetchUserActivitySpark(
  userId: string,
): Promise<{ buckets: ActivitySparkBucket[] }> {
  const res = await get<{ buckets: ActivitySparkBucket[] }>(
    `/admin/users/${encodeURIComponent(userId)}/activity-spark`,
  );
  return res.data;
}

export interface BalanceHistoryBucket {
  day: string;
  balanceNano: string;
  deltaNano: string;
}

export async function fetchUserBalanceHistory(
  userId: string,
): Promise<{ buckets: BalanceHistoryBucket[] }> {
  const res = await get<{ buckets: BalanceHistoryBucket[] }>(
    `/admin/users/${encodeURIComponent(userId)}/balance-history`,
  );
  return res.data;
}

export type ActivityLogKind =
  | 'session_started'
  | 'session_ended'
  | 'credit_grant'
  | 'credit_debit'
  | 'admin_action'
  | 'byok_added'
  | 'byok_removed';

export interface ActivityLogItem {
  at: string;
  kind: string;
  summary: string;
  refId: string | null;
  metadata: Record<string, unknown>;
}

export async function fetchUserActivityLog(
  userId: string,
  opts: { cursor?: string; limit?: number } = {},
): Promise<{ items: ActivityLogItem[]; nextCursor: string | undefined }> {
  const params = new URLSearchParams();
  if (opts.cursor) params.set('cursor', opts.cursor);
  if (opts.limit) params.set('limit', String(opts.limit));
  const qs = params.toString();
  const res = await get<{ items: ActivityLogItem[] }>(
    `/admin/users/${encodeURIComponent(userId)}/activity-log${qs ? `?${qs}` : ''}`,
  );
  return { items: res.data.items, nextCursor: res.meta?.nextCursor };
}

// ─── Seguridad — historial de accesos + sesiones activas ─────────────────

export interface AuthEventItem {
  id: string;
  eventType: string;
  appSource: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  deviceType: string | null;
  browser: string | null;
  os: string | null;
  country: string | null;
  city: string | null;
  failureReason: string | null;
  emailTried: string | null;
  createdAt: string;
}

export async function fetchUserAuthEvents(
  userId: string,
  opts: { cursor?: string; limit?: number } = {},
): Promise<{ items: AuthEventItem[]; nextCursor: string | undefined }> {
  const params = new URLSearchParams();
  if (opts.cursor) params.set('cursor', opts.cursor);
  if (opts.limit) params.set('limit', String(opts.limit));
  const qs = params.toString();
  const res = await get<{ items: AuthEventItem[] }>(
    `/admin/users/${encodeURIComponent(userId)}/auth-events${qs ? `?${qs}` : ''}`,
  );
  return { items: res.data.items, nextCursor: res.meta?.nextCursor };
}

export type SessionStatus = 'active' | 'revoked' | 'expired';

export interface SessionItem {
  id: string;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  expiresAt: string;
  revokedAt: string | null;
  status: SessionStatus;
}

export async function fetchUserSessions(userId: string): Promise<{ items: SessionItem[] }> {
  const res = await get<{ items: SessionItem[] }>(
    `/admin/users/${encodeURIComponent(userId)}/sessions`,
  );
  return res.data;
}

export async function revokeUserSession(
  userId: string,
  target: { sessionId: string } | { all: true },
): Promise<{ revoked: number }> {
  return postJsonAdmin(
    `/admin/users/${encodeURIComponent(userId)}/sessions/revoke`,
    'all' in target ? { all: true } : { sessionId: target.sessionId },
  );
}

// ─── BO-5 C — mutations ───────────────────────────────────────────────────

export interface ImpersonateTargetOption {
  id: string;
  label: string;
}

/** Apps a las que el BO puede entrar como el user (selector del impersonate). */
export async function fetchImpersonateTargets(): Promise<ImpersonateTargetOption[]> {
  const res = await get<{ targets: ImpersonateTargetOption[] }>('/admin/impersonate/targets');
  return res.data.targets;
}

/**
 * Crea un grant de un solo uso y devuelve el `redeemUrl`. El BO lo abre en una
 * pestaña nueva (`window.open`) → entra a la app destino logueado como el user,
 * SIN tocar la sesión del admin en el BO.
 */
export async function startImpersonate(
  userId: string,
  app: string,
  ttlMinutes?: number,
): Promise<{ redeemUrl: string; app: string; ttlMinutes: number }> {
  return postJsonAdmin('/admin/impersonate/start', {
    userId,
    app,
    ...(ttlMinutes ? { ttlMinutes } : {}),
  });
}

/**
 * Reset destructivo: deja la cuenta "como recién registrada". Borra historial,
 * memoria, progreso y billing; el balance vuelve al welcome grant. Preserva el
 * login. Admin-only.
 */
export async function resetUser(
  userId: string,
): Promise<{ userId: string; balanceAfterNano: string }> {
  return postJsonAdmin(`/admin/users/${encodeURIComponent(userId)}/reset`, {});
}

export async function resetUserPassword(
  userId: string,
): Promise<{ userId: string; token: string; expiresAt: string }> {
  return postJsonAdmin(`/admin/users/${encodeURIComponent(userId)}/reset-password`, {});
}

export async function suspendUser(
  userId: string,
  reason: string,
): Promise<{ userId: string; suspendedAt: string }> {
  return postJsonAdmin(`/admin/users/${encodeURIComponent(userId)}/suspend`, { reason });
}

export async function unsuspendUser(userId: string): Promise<{ userId: string }> {
  return postJsonAdmin(`/admin/users/${encodeURIComponent(userId)}/unsuspend`, {});
}

export async function issueUserCredits(
  userId: string,
  amountNano: string,
  reason: string,
): Promise<{ userId: string; amountNano: string; balanceAfterNano: string }> {
  return postJsonAdmin(`/admin/users/${encodeURIComponent(userId)}/issue-credits`, {
    amountNano,
    reason,
  });
}
